import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { getToken } from '../../services/tokenService';
import { useAuth } from '../../contexts/AuthContext';
import { BACKEND_URL, DEFAULT_MESSAGES, ROLES } from './invitationConstants';

export default function useInvitationDialog({ isOpen, prefilledRole = null, onSuccess }) {
  const { user: authUser } = useAuth();
  const isSuperAdmin = authUser?.role === 'super_admin';
  const [activeTab, setActiveTab] = useState('formulaire');
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [emailPreview, setEmailPreview] = useState(null);
  const [invitationResult, setInvitationResult] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [historique, setHistorique] = useState([]);
  const [loadingHistorique, setLoadingHistorique] = useState(false);

  const [messagesPredefinis, setMessagesPredefinis] = useState(DEFAULT_MESSAGES);
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const [showNewMessageForm, setShowNewMessageForm] = useState(false);
  const [newMessageData, setNewMessageData] = useState({ titre: '', contenu: '', role_cible: '' });
  const [savingMessage, setSavingMessage] = useState(false);

  const [emailCheckResult, setEmailCheckResult] = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(false);

  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    email: '',
    role: prefilledRole || 'membre',
    telephone: '',
    entreprise: '',
    message_personnalise: '',
    envoyer_email: true
  });

  const checkEmailExists = async (email) => {
    if (!email || !email.includes('@')) {
      setEmailCheckResult(null);
      return;
    }
    try {
      setCheckingEmail(true);
      const token = getToken();
      const response = await axios.get(
        `${BACKEND_URL}/api/invitations/check-email/${encodeURIComponent(email)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEmailCheckResult(response.data);
    } catch {
      setEmailCheckResult(null);
    } finally {
      setCheckingEmail(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.email && formData.email.includes('@')) {
        checkEmailExists(formData.email);
      } else {
        setEmailCheckResult(null);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.email]);

  const loadMessagesPredefinis = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/invitations/messages-predefinis`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (response.data.messages && response.data.messages.length > 0) {
        setMessagesPredefinis(response.data.messages);
      }
    } catch {
      // Garder les messages par défaut
    }
  };

  useEffect(() => {
    if (isOpen) {
      setInvitationResult(null);
      setEmailPreview(null);
      setActiveTab('formulaire');
      setSelectedMessageId(null);
      setShowNewMessageForm(false);
      loadMessagesPredefinis();
      if (prefilledRole) {
        setFormData(prev => ({ ...prev, role: prefilledRole }));
      }
    }
  }, [isOpen, prefilledRole]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSelectMessage = (messageId) => {
    if (messageId === selectedMessageId) {
      setSelectedMessageId(null);
      handleInputChange('message_personnalise', '');
    } else {
      const message = messagesPredefinis.find(m => m.id === messageId);
      if (message) {
        setSelectedMessageId(messageId);
        handleInputChange('message_personnalise', message.contenu);
      }
    }
  };

  const handleCreateMessage = async () => {
    if (!newMessageData.titre || !newMessageData.contenu) {
      toast.error('Veuillez remplir le titre et le contenu');
      return;
    }
    try {
      setSavingMessage(true);
      await axios.post(
        `${BACKEND_URL}/api/invitations/messages-predefinis`,
        {
          titre: newMessageData.titre,
          contenu: newMessageData.contenu,
          role_cible: newMessageData.role_cible || null
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success('Message prédéfini créé');
      setShowNewMessageForm(false);
      setNewMessageData({ titre: '', contenu: '', role_cible: '' });
      loadMessagesPredefinis();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSavingMessage(false);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (messageId.startsWith('default-')) {
      toast.error('Impossible de supprimer un message par défaut');
      return;
    }
    try {
      await axios.delete(
        `${BACKEND_URL}/api/invitations/messages-predefinis/${messageId}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success('Message supprimé');
      loadMessagesPredefinis();
      if (selectedMessageId === messageId) {
        setSelectedMessageId(null);
        handleInputChange('message_personnalise', '');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const loadPreview = async () => {
    if (!formData.prenom || !formData.nom) {
      toast.error('Veuillez renseigner le prénom et le nom');
      return;
    }
    try {
      setPreviewLoading(true);
      const token = getToken();
      const params = new URLSearchParams({
        prenom: formData.prenom,
        nom: formData.nom,
        role: formData.role,
        email: formData.email || 'exemple@email.fr',
        ...(formData.message_personnalise && { message: formData.message_personnalise })
      });
      const response = await axios.get(
        `${BACKEND_URL}/api/invitations/preview?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEmailPreview(response.data);
      setActiveTab('apercu');
    } catch {
      toast.error('Erreur lors de la prévisualisation');
    } finally {
      setPreviewLoading(false);
    }
  };

  const loadHistorique = async () => {
    try {
      setLoadingHistorique(true);
      const token = getToken();
      const response = await axios.get(
        `${BACKEND_URL}/api/invitations/historique`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHistorique(response.data);
    } catch {
    } finally {
      setLoadingHistorique(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.prenom || !formData.nom || !formData.email) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (!formData.email.includes('@')) {
      toast.error('Veuillez saisir un email valide');
      return;
    }
    try {
      setLoading(true);
      const token = getToken();
      const response = await axios.post(
        `${BACKEND_URL}/api/invitations/envoyer`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInvitationResult(response.data);
      setActiveTab('resultat');
      if (response.data.email_envoye) {
        toast.success('Invitation envoyée avec succès !');
      } else {
        toast.success('Compte créé ! Email non envoyé (SMTP non configuré)');
      }
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (invitationId) => {
    try {
      const token = getToken();
      await axios.post(
        `${BACKEND_URL}/api/invitations/renvoyer/${invitationId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Invitation renvoyée avec succès');
      loadHistorique();
    } catch {
      toast.error('Erreur lors du renvoi');
    }
  };

  const [checkingDelivery, setCheckingDelivery] = useState(null);

  const handleCorrigerRenvoyer = async (invitationId, newEmail) => {
    if (!newEmail || !newEmail.includes('@')) {
      toast.error('Saisissez une adresse email valide');
      return false;
    }
    try {
      const token = getToken();
      await axios.post(
        `${BACKEND_URL}/api/invitations/${invitationId}/corriger-renvoyer`,
        { email: newEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Invitation renvoyée à ${newEmail}`);
      setHistorique(prev => prev.map(inv => inv.id === invitationId
        ? { ...inv, email: newEmail, delivery: { ...(inv.delivery || {}), bounced: false, stage: 'sent' } }
        : inv));
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la correction');
      return false;
    }
  };

  const handleCheckDelivery = async (invitationId) => {
    try {
      setCheckingDelivery(invitationId);
      const token = getToken();
      const res = await axios.post(
        `${BACKEND_URL}/api/invitations/${invitationId}/refresh-delivery`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const delivery = res.data?.delivery;
      setHistorique(prev => prev.map(inv => inv.id === invitationId ? { ...inv, delivery } : inv));
      if (!res.data?.found) {
        toast.info('Aucun email trouvé côté Brevo pour ce destinataire');
      } else if (delivery?.bounced) {
        toast.error(`Email rejeté (${delivery.bounce_reason || 'bounce'})`);
      } else if (delivery?.opened || delivery?.clicked) {
        toast.success('Email bien reçu et ouvert par le destinataire');
      } else if (delivery?.delivered) {
        toast.success('Email délivré dans la boîte du destinataire');
      } else {
        toast.info('Statut : envoyé (pas encore de confirmation de réception)');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la vérification');
    } finally {
      setCheckingDelivery(null);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copié dans le presse-papiers');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const getRoleInfo = (roleValue) => {
    return ROLES.find(r => r.value === roleValue) || ROLES[2];
  };

  const resetForm = () => {
    setFormData({
      prenom: '',
      nom: '',
      email: '',
      role: prefilledRole || 'membre',
      telephone: '',
      entreprise: '',
      message_personnalise: '',
      envoyer_email: true
    });
    setInvitationResult(null);
    setEmailPreview(null);
    setEmailCheckResult(null);
    setActiveTab('formulaire');
  };

  return {
    isSuperAdmin,
    activeTab, setActiveTab,
    loading, previewLoading,
    emailPreview, invitationResult,
    copiedField, historique, loadingHistorique,
    messagesPredefinis, selectedMessageId, setSelectedMessageId,
    showNewMessageForm, setShowNewMessageForm,
    newMessageData, setNewMessageData, savingMessage,
    emailCheckResult, checkingEmail,
    formData, handleInputChange,
    handleSelectMessage, handleCreateMessage, handleDeleteMessage,
    loadPreview, loadHistorique, handleSubmit, handleResend,
    copyToClipboard, getRoleInfo, resetForm,
    checkingDelivery, handleCheckDelivery, handleCorrigerRenvoyer,
  };
}
